import {
  Activity,
  BarChart3,
  BookOpen,
  Coins,
  Contact,
  CreditCard,
  FileText,
  Globe,
  Image,
  LayoutDashboard,
  ListOrdered,
  Package,
  Search,
  Send,
  Settings,
  ShieldBan,
  ShieldCheck,
  Smartphone,
  TicketPercent,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { AdminBadges } from "@ewn/shared";

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Which of the menu's counts shows beside this link, if any. */
  badge?: keyof AdminBadges;
  /** Exact match, for a route that is also another route's prefix. */
  end?: boolean;
}

export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

/**
 * The menu, grouped.
 *
 * A flat list of this length is past the point where scanning works -- the eye
 * lands somewhere in the middle and starts reading. Five groups of two to five
 * is a glance.
 *
 * NO TWO LINKS SHARE AN ICON. The icon is what the eye lands on at 252px, so
 * two links sharing one look like the same link -- Customers and
 * Administrators are both people and are `Contact` and `Users` for exactly
 * that reason.
 *
 * A GROUP HEADING MAY REPEAT ONE OF ITS OWN LINKS' ICONS, and four do (Sales
 * and Orders, Customers and Customers, Shop and Settings, Diagnostics and
 * Logs). That is the heading saying what is inside it, one level up, not two
 * destinations that look alike -- a heading is not somewhere you go.
 *
 * The grouping is by WHAT THE OPERATOR IS DOING, not by which table the data
 * lives in: everything that moves money is together, the people and the lines
 * they hold are together, everything that changes what the site says is
 * together, and the things you only open when something is wrong are at the
 * bottom.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    id: "sales",
    label: "Sales",
    icon: ListOrdered,
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/stats", label: "Stats", icon: BarChart3 },
      { to: "/orders", label: "Orders", icon: ListOrdered, badge: "orders" },
      { to: "/payments", label: "Payments", icon: CreditCard },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    icon: Contact,
    items: [
      { to: "/customers", label: "Customers", icon: Contact },
      { to: "/esims", label: "eSIMs", icon: Smartphone },
    ],
  },
  {
    id: "content",
    label: "Content",
    icon: FileText,
    items: [
      { to: "/seo", label: "SEO", icon: Search },
      { to: "/articles", label: "Articles", icon: BookOpen },
      { to: "/assets", label: "Images", icon: Image },
      { to: "/translations", label: "Translations", icon: Globe },
      { to: "/publish", label: "Publish", icon: Send },
    ],
  },
  {
    id: "shop",
    label: "Shop",
    icon: Settings,
    items: [
      { to: "/products", label: "Products", icon: Package },
      { to: "/coins", label: "Coins", icon: Coins },
      { to: "/coupons", label: "Coupons", icon: TicketPercent },
      { to: "/settings", label: "Settings", icon: Settings },
      { to: "/admins", label: "Administrators", icon: Users },
    ],
  },
  {
    // "When something is wrong" said the intent better and did not FIT: at
    // 252px the label truncated to "When something is w..." in every
    // screenshot. A label that is always cut off is not a label, and widening
    // the sidebar to rescue one word is the wrong trade.
    id: "trouble",
    label: "Diagnostics",
    icon: Activity,
    items: [
      { to: "/blocks", label: "Blocked networks", icon: ShieldBan },
      { to: "/audit", label: "Audit trail", icon: ShieldCheck },
      { to: "/logs", label: "Logs", icon: Activity },
    ],
  },
];

/** The group and item owning a path, so the shell can open the right menu. */
export function findRoute(pathname: string): { group: NavGroup; item: NavItem } | null {
  let best: { group: NavGroup; item: NavItem } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const hit = item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`);
      // The LONGEST match wins: /orders and /orders/ABC1234 both match the
      // Orders link, and a shorter accidental prefix must not beat it.
      if (hit && (!best || item.to.length > best.item.to.length)) best = { group, item };
    }
  }
  return best;
}

/**
 * The count a screen clears, if any: a link's own list, at its exact path and
 * unfiltered. One order, a search or a status filter is a look at SOME of
 * them, not at the new ones, and clears nothing.
 */
export function badgeClearedAt(pathname: string, search: string): keyof AdminBadges | undefined {
  const here = findRoute(pathname);
  return here && pathname === here.item.to && !search ? here.item.badge : undefined;
}
